import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import {  formatNumberWithoutFraction } from "../utility";

type SalesOffice = {
  region: string;
  sales_office_id: string;
  sales_office_name: string;
  target: string;
  achieved_target: string;
};

type Props = {
  sales_offices: SalesOffice[];
  barColors: any;
};

const SalesRegionChart: React.FC<Props> = ({ sales_offices, barColors }) => {
  const chartData = sales_offices.map((office) => ({
    id: office?.sales_office_id,
    sales_office: office?.sales_office_name,
    region: office?.region,
    Target: (parseInt(office?.target)),
    Achieved: parseInt(office?.achieved_target),
  }));

  const values = chartData?.map((d: any) => d.Target);
  if (values.every((value: any) => value === 0)) {
    return <div></div>;
  }

  const maxValue = Math.max(...values);

  const calculateStepSize = (maxValue: number) => {
    const range = maxValue;
    const magnitude = Math.pow(10, Math.floor(Math.log10(range)));
    const normalizedRange = range / magnitude;
    let step;

    if (normalizedRange <= 1) {
      step = 0.2;
    } else if (normalizedRange <= 2) {
      step = 0.5;
    } else if (normalizedRange <= 5) {
      step = 1;
    } else {
      step = 2;
    }

    return step * magnitude;
  };

  const stepSize = calculateStepSize(maxValue);
  const tickValues = [];
  for (let i = 0; i <= maxValue + stepSize; i += stepSize) {
    tickValues.push(Number(i.toFixed(2)));
  }

  const barMinWidth = 130;
  const chartWidth = chartData.length * barMinWidth;

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <div style={{ minWidth: `${chartWidth}px`, height: '400px' }}>
        <ResponsiveBar
          data={chartData}
          keys={['Target', 'Achieved']}
          indexBy="sales_office"
          margin={{ top: 50, right: 30, bottom: 100, left: 60 }}
          padding={0.4}
          groupMode="grouped"
          colors={({ id }) =>
            id === 'Achieved' ? '#00C48C' : id === 'Target' ? '#FFAA2A' : '#ccc'
          }
          theme={{
            axis: {
              ticks: {
                text: {
                  fontSize: 10,
                  fontWeight: 600,
                  fill: '#333',
                },
              },
              legend: {
                text: {
                  fontSize: 14,
                  fontWeight: 600,
                  fill: '#333',
                },
              },
            },
            labels: {
              text: {
                fontSize: 14,
                fontWeight: 600,
                fill: '#000',
              },
            },
            legends: {
              text: {
                fontSize: 14,
                fontWeight: 600,
                fill: '#333',
              },
            },
            tooltip: {
              container: {
                fontSize: 13,
                fontWeight: 600,
              },
            },
          }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Sales Office',
            legendPosition: 'middle',
            legendOffset: 80,
            format: (value: string) => {
              const region = chartData.find(d => d.sales_office === value)?.region || '';
              return value ? `${value}\n${region}` : 'All Offices';
            }
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            legend: 'Values',
            legendPosition: 'middle',
            legendOffset: -50,
            tickValues: tickValues,
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          label={({ value }) => formatNumberWithoutFraction(value)}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'bottom',
              direction: 'row',
              translateY: 60,
              itemWidth: 80,
              itemHeight: 20,
              symbolSize: 12,
            }

          ]} tooltip={({ id, value, indexValue }) => (
            <div className='text-[13px] font-semibold' style={{ padding: '6px 9px', background: 'white', border: '1px solid #ccc' }}>
              {indexValue && <>
                <strong>{indexValue}</strong><br />
              </>}
              {id}: {formatNumberWithoutFraction(value)}
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default SalesRegionChart;
